import { isPointThreshold } from '../../core/types/type-guards.js';
import { getYValue } from '../components/thresholds/builder.js';
import {
  XYAxisTypeLinear,
  XYAxisTypeCategorical,
  XYAxisTypeTime,
  XYAxisTypeLog,
  type XYAxisAnyNumericalType,
} from '../types/axis.js';
import { ScaleTypesMap } from '../types/scale-types-map.js';
import {
  XYAccessorDataTypes,
  type XYChartThresholdDataLineDynamic,
  type XYChartThresholdDataRangeDynamic,
  type XYChartThresholdDynamicData,
  type XYChartThresholdStaticData,
} from '../types/xy-chart.js';

function isValidDate(d: Date) {
  return d instanceof Date && !isNaN(d.valueOf());
}

type Constructors = 'Number' | 'String' | 'Date';
const castValueToNumber = {
  Number: (value: XYAccessorDataTypes) => value,
  String: (value: XYAccessorDataTypes) => {
    const castValue = parseFloat(value as string);
    return isNaN(castValue) ? undefined : castValue;
  },
  Date: (value: XYAccessorDataTypes) => (value as Date).getTime(),
};

const castValueToString = {
  Number: (value: XYAccessorDataTypes) => value.toString(),
  String: (value: XYAccessorDataTypes) => value,
  Date: (value: XYAccessorDataTypes) => (value as Date).toISOString(),
};

const castValueToDate = {
  Number: (value: XYAccessorDataTypes) => {
    const castValue = new Date(value as number);
    return isValidDate(castValue) ? castValue : undefined;
  },
  String: (value: XYAccessorDataTypes) => {
    const castValue = new Date(value as string);
    return isValidDate(castValue) ? castValue : undefined;
  },
  Date: (value: XYAccessorDataTypes) =>
    isValidDate(value as Date) ? value : undefined,
};

const castsMap = {
  [XYAxisTypeLinear]: castValueToNumber,
  [XYAxisTypeCategorical]: castValueToString,
  [XYAxisTypeTime]: castValueToDate,
  [XYAxisTypeLog]: castValueToNumber,
};

export function castDataType<T extends keyof ScaleTypesMap>(
  value: XYAccessorDataTypes | undefined,
  axisType: T,
): ScaleTypesMap[T]['castDataType'] {
  return castsMap[axisType][value?.constructor.name as Constructors]?.(
    value as XYAccessorDataTypes,
  );
}

export function castDynamicThresholdsDataType(
  data: XYChartThresholdDynamicData[],
  xAxisType: XYAxisAnyNumericalType,
  yAxisType: XYAxisAnyNumericalType,
): (XYChartThresholdDataLineDynamic | XYChartThresholdDataRangeDynamic)[] {
  return data.reduce((acc, d) => {
    const x = castDataType(d.x, xAxisType);
    const yMin = castDataType(getYValue(d, 'min'), yAxisType);
    const yMax = castDataType(getYValue(d, 'max'), yAxisType);

    const isValidThreshold =
      x !== undefined && yMin !== undefined && yMax !== undefined;

    if (isValidThreshold) {
      if (isPointThreshold(yMin, yMax)) {
        acc.push({ x, y: yMin });
      } else {
        acc.push({ x, yMin, yMax });
      }
    }
    return acc;
  }, [] as (XYChartThresholdDataLineDynamic | XYChartThresholdDataRangeDynamic)[]);
}

export function castStaticThresholdsDataType(
  data: XYChartThresholdStaticData,
  yAxisType: XYAxisAnyNumericalType,
) {
  const yMin = castDataType(getYValue(data, 'min'), yAxisType);
  const yMax = castDataType(getYValue(data, 'max'), yAxisType);

  const isValidThreshold = yMin !== undefined && yMax !== undefined;

  if (isValidThreshold) {
    if (isPointThreshold(yMin, yMax)) {
      return { y: yMin };
    } else {
      return { yMin, yMax };
    }
  }

  return undefined;
}
